import { useState, useEffect, useRef } from "react";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, style = {}, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(24px)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

const FEATURES = [
  { icon: "◈", title: "Bill Search & Tracking", desc: "Search all 3,200+ bills in the current session. Track the ones that matter, add private notes, nicknames, and stances — and filter by chamber, status, or keyword instantly." },
  { icon: "⬡", title: "Legislative Calendar", desc: "Committee meetings, fundraiser events, and session activity in one unified view — with location, agenda bills, and add-to-calendar in a single click." },
  { icon: "◎", title: "Bill Studio", desc: "Upload any Michigan bill and get an instant plain-language summary, stakeholder analysis, fiscal implications, and a full breakdown of how the legislation works in practice." },
  { icon: "⬙", title: "Client Portal", desc: "Manage your full book of business — track bills per client, log meetings and activity, assign positions, and generate professional PDF reports in seconds." },
  { icon: "▣", title: "Campaign Finance & District Maps", desc: "Search Michigan campaign finance records across all candidates and PACs. Explore interactive district maps for all 110 House and 38 Senate members." },
  { icon: "◆", title: "Directory", desc: "A fully searchable directory of Michigan state agencies, sub-agencies, lobbying firms, registered lobbyists, and top associations — all in one place." },
];

const TESTIMONIALS = [
  { quote: "WhipCount™ replaced five different tools I was paying for. The calendar, bill tracker, and fundraiser view alone save me hours every week during session.", author: "Senior Lobbyist", org: "Michigan Government Affairs Firm" },
  { quote: "The Bill Studio is remarkable. I can draft testimony, analyze a bill, and build a lobby strategy in the time it used to take me just to read the bill.", author: "Government Affairs Director", org: "Michigan Trade Association" },
  { quote: "Having committee meetings and fundraiser events in the same calendar view — with location and RSVP info — is something I didn't know I needed until I had it.", author: "Principal, Government Relations", org: "Michigan Corporation" },
];

const FAQS = [
  { q: "What tools are included in the subscription?", a: "WhipCount™ is a full legislative intelligence suite — Legislative Calendar (committee meetings + fundraiser events), Bill Search & Tracking, Floor Vote Simulator, Bill Studio (six AI tools: analyzer, creator, lobby strategist, testimony writer, amendment builder, and floor vote), Legislator Profiles, Campaign Finance data, District Maps, and News Feed." },

  { q: "Does WhipCount™ replace my current tools?", a: "For most Michigan government affairs professionals, yes. WhipCount™ consolidates bill tracking, legislative calendaring, fundraiser event management, legislator research, campaign finance lookups, district mapping, AI drafting tools, and news monitoring into a single platform — eliminating the need to juggle multiple subscriptions and browser tabs during session." },
  { q: "Is my data secure?", a: "All sessions are encrypted and private. Your bill drafts, strategy sessions, tracked bills, and notes are not shared with any third party and are not used to train public AI models." },
  { q: "Can multiple people use one subscription?", a: "The $99/month plan covers a single-user professional license. Contact admin@whipcount.us for enterprise multi-seat pricing." },
  { q: "Can I cancel anytime?", a: "Yes. Subscriptions are month-to-month with no long-term commitment. Cancel before your next billing date and you will not be charged again." },
  { q: "When will payment processing be available?", a: "We are finalizing payment infrastructure. Submit your information now to secure your place and be the first notified when billing goes live." },
];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700;1,800&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{scroll-behavior:smooth;}
:root{
  --bg:#09090e;--surface:#111118;--surface2:#18181f;--charcoal:#1e1e28;
  --gold:#c9a84c;--gold-dim:#8a6830;--gold-glow:rgba(201,168,76,0.12);--gold-glow2:rgba(201,168,76,0.06);
  --text:#e6e4dc;--muted:#6a6860;--border:rgba(255,255,255,0.06);
  --green:#2e7d52;--green-glow:rgba(46,125,82,0.15);
  --font-display:'Playfair Display',serif;
  --font-body:'IBM Plex Sans',sans-serif;
  --font-mono:'IBM Plex Mono',monospace;
}
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}

/* NAV */
.nav{position:sticky;top:0;z-index:100;background:rgba(9,9,14,0.92);backdrop-filter:blur(16px);border-bottom:1px solid var(--border);padding:1rem 5vw;display:flex;align-items:center;justify-content:space-between;}
.nav-logo{font-family:var(--font-display);font-size:1.15rem;font-weight:800;color:var(--text);cursor:pointer;}
.nav-logo em{font-style:normal;color:var(--gold);}
.nav-logo sup{font-family:var(--font-body);font-size:0.5rem;color:var(--muted);vertical-align:super;}
.nav-back{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.2s;}
.nav-back:hover{border-color:var(--gold-dim);color:var(--gold);}

/* HERO */
.hero{padding:7rem 5vw 5rem;text-align:center;position:relative;overflow:hidden;}
.hero-bg{position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse 80% 60% at 50% 0%,rgba(201,168,76,0.08) 0%,transparent 70%);}
.hero-grid{position:absolute;inset:0;background-image:linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px);background-size:70px 70px;mask-image:radial-gradient(ellipse 80% 80% at 50% 50%,black,transparent);pointer-events:none;}
.hero-inner{position:relative;z-index:1;max-width:860px;margin:0 auto;}
.hero-eyebrow{display:inline-flex;align-items:center;gap:0.5rem;background:var(--surface);border:1px solid var(--border);border-radius:100px;padding:0.35rem 1rem;font-size:0.72rem;font-weight:500;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:2rem;font-family:var(--font-mono);}
.hero-eyebrow-dot{width:6px;height:6px;border-radius:50%;background:var(--gold);animation:pulse 2s infinite;}
@keyframes pulse{0%,100%{opacity:0.5;transform:scale(1)}50%{opacity:1;transform:scale(1.1)}}
.hero h1{font-family:var(--font-display);font-size:clamp(2.8rem,6vw,5rem);font-weight:900;line-height:1.05;letter-spacing:-0.03em;color:var(--text);margin-bottom:1.5rem;}
.hero h1 em{font-style:italic;color:var(--gold);}
.hero-sub{font-size:1.1rem;color:var(--muted);max-width:600px;margin:0 auto 2.5rem;line-height:1.75;font-weight:300;}
.hero-price-tag{display:inline-flex;align-items:baseline;gap:0.5rem;margin-bottom:2.5rem;}
.hero-price{font-family:var(--font-display);font-size:3.5rem;font-weight:900;color:var(--gold);}
.hero-price-period{font-size:1rem;color:var(--muted);font-weight:300;}
.btn-subscribe{display:inline-block;background:var(--gold);color:#09090e;border:none;border-radius:8px;padding:1rem 2.5rem;font-family:var(--font-body);font-size:1.05rem;font-weight:500;cursor:pointer;letter-spacing:0.02em;transition:background 0.2s,transform 0.15s,box-shadow 0.2s;text-decoration:none;}
.btn-subscribe:hover{background:#d4b05a;transform:translateY(-2px);box-shadow:0 12px 40px var(--gold-glow);}
.hero-note{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);margin-top:1rem;letter-spacing:0.06em;}

/* DIVIDER */
.divider{height:1px;background:linear-gradient(90deg,transparent,var(--border),transparent);margin:0;}

/* SECTION */
.section{padding:6rem 5vw;}
.section-inner{max-width:1100px;margin:0 auto;}
.section-tag{display:inline-block;font-family:var(--font-mono);font-size:0.68rem;color:var(--gold);letter-spacing:0.15em;text-transform:uppercase;margin-bottom:1rem;padding-bottom:0.3rem;border-bottom:1px solid var(--gold-dim);}
.section-title{font-family:var(--font-display);font-size:clamp(1.8rem,3.5vw,2.8rem);font-weight:800;letter-spacing:-0.02em;line-height:1.1;margin-bottom:1rem;color:var(--text);}
.section-sub{font-size:1rem;color:var(--muted);max-width:520px;line-height:1.75;font-weight:300;}

/* SOCIAL PROOF BAR */
.proof-bar{background:var(--surface);border-top:1px solid var(--border);border-bottom:1px solid var(--border);padding:1.5rem 5vw;}
.proof-inner{max-width:1100px;margin:0 auto;display:flex;align-items:center;justify-content:space-around;gap:2rem;flex-wrap:wrap;}
.proof-stat{text-align:center;}
.proof-num{font-family:var(--font-display);font-size:2rem;font-weight:800;color:var(--gold);}
.proof-label{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-top:0.2rem;}

/* FEATURE GRID */
.feature-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;margin-top:3.5rem;border:1px solid var(--border);border-radius:12px;overflow:hidden;}
.feature-card{background:var(--bg);padding:2rem 1.75rem;transition:background 0.2s;}
.feature-card:hover{background:var(--charcoal);}
.feature-icon{font-size:1.4rem;color:var(--gold);display:block;margin-bottom:1rem;}
.feature-title{font-family:var(--font-display);font-size:1.05rem;font-weight:700;margin-bottom:0.6rem;color:var(--text);}
.feature-desc{font-size:0.85rem;color:var(--muted);line-height:1.65;font-weight:300;}

/* VALUE PROP */
.value-section{background:var(--surface);}
.value-grid{display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center;margin-top:3rem;}
.value-list{list-style:none;display:flex;flex-direction:column;gap:1.25rem;}
.value-item{display:flex;gap:1rem;align-items:flex-start;}
.value-check{width:22px;height:22px;border-radius:5px;background:var(--gold-glow);border:1px solid var(--gold-dim);display:flex;align-items:center;justify-content:center;color:var(--gold);font-size:0.75rem;flex-shrink:0;margin-top:1px;}
.value-text strong{color:var(--text);font-weight:500;display:block;font-size:0.95rem;margin-bottom:0.2rem;}
.value-text span{color:var(--muted);font-size:0.83rem;line-height:1.6;}
.value-callout{background:var(--charcoal);border:1px solid var(--border);border-radius:16px;padding:2.5rem;position:relative;overflow:hidden;}
.value-callout::before{content:'';position:absolute;top:-40px;right:-40px;width:200px;height:200px;border-radius:50%;background:var(--gold-glow2);pointer-events:none;}
.callout-price{font-family:var(--font-display);font-size:3rem;font-weight:900;color:var(--gold);line-height:1;}
.callout-period{font-size:0.9rem;color:var(--muted);margin-bottom:1.5rem;}
.callout-items{list-style:none;display:flex;flex-direction:column;gap:0.75rem;margin-bottom:2rem;}
.callout-item{font-size:0.85rem;color:var(--text);display:flex;gap:0.6rem;align-items:center;}
.callout-item::before{content:'✓';color:var(--gold);font-weight:700;}
.callout-note{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);letter-spacing:0.05em;text-align:center;margin-top:1rem;}

/* TESTIMONIALS */
.testimonial-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem;margin-top:3.5rem;}
.testimonial-card{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:1.75rem;}
.testimonial-quote{font-size:0.9rem;color:var(--text);line-height:1.7;font-style:italic;margin-bottom:1.25rem;font-family:var(--font-display);}
.testimonial-author{font-size:0.78rem;font-weight:500;color:var(--gold);}
.testimonial-org{font-size:0.72rem;color:var(--muted);font-family:var(--font-mono);}

/* FAQ */
.faq-list{margin-top:3rem;display:flex;flex-direction:column;gap:0;}
.faq-item{border-bottom:1px solid var(--border);}
.faq-q{width:100%;background:none;border:none;color:var(--text);text-align:left;padding:1.25rem 0;font-family:var(--font-body);font-size:0.95rem;font-weight:500;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:1rem;}
.faq-q:hover{color:var(--gold);}
.faq-icon{font-size:1.2rem;color:var(--muted);flex-shrink:0;transition:transform 0.2s;}
.faq-icon.open{transform:rotate(45deg);color:var(--gold);}
.faq-a{overflow:hidden;max-height:0;transition:max-height 0.3s ease,padding 0.3s ease;padding:0 0;}
.faq-a.open{max-height:200px;padding:0 0 1.25rem;}
.faq-a p{font-size:0.88rem;color:var(--muted);line-height:1.7;font-weight:300;}

/* SUBSCRIBE FORM */
.subscribe-section{background:var(--surface);}
.form-card{max-width:560px;margin:3rem auto 0;background:var(--charcoal);border:1px solid var(--border);border-radius:16px;padding:2.5rem;}
.form-title{font-family:var(--font-display);font-size:1.5rem;font-weight:800;color:var(--text);margin-bottom:0.5rem;letter-spacing:-0.02em;}
.form-sub{font-size:0.85rem;color:var(--muted);margin-bottom:2rem;line-height:1.6;}
.form-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;}
.form-full{grid-column:1/-1;}
.field{display:flex;flex-direction:column;gap:0.4rem;}
.field label{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;}
.field input,.field select{background:var(--surface);border:1px solid var(--border);border-radius:6px;padding:0.7rem 0.9rem;color:var(--text);font-family:var(--font-body);font-size:0.88rem;outline:none;transition:border-color 0.2s;-webkit-appearance:none;}
.field input:focus,.field select:focus{border-color:var(--gold-dim);}
.field input::placeholder{color:var(--muted);}
.form-notice{background:var(--surface);border:1px solid var(--border);border-radius:8px;padding:0.85rem 1rem;font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);line-height:1.6;margin-top:1rem;letter-spacing:0.03em;}
.form-notice strong{color:var(--gold);}
.btn-submit{width:100%;background:var(--gold);color:#09090e;border:none;border-radius:8px;padding:0.95rem;font-family:var(--font-body);font-size:1rem;font-weight:500;cursor:pointer;margin-top:1.25rem;transition:background 0.2s,transform 0.15s;letter-spacing:0.02em;}
.btn-submit:hover{background:#d4b05a;transform:translateY(-1px);}
.success-card{text-align:center;padding:2rem 1rem;}
.success-icon{font-size:3rem;margin-bottom:1rem;}
.success-title{font-family:var(--font-display);font-size:1.5rem;font-weight:800;color:var(--gold);margin-bottom:0.75rem;}
.success-sub{font-size:0.9rem;color:var(--muted);line-height:1.7;}

/* FOOTER */
footer{border-top:1px solid var(--border);padding:2rem 5vw;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem;}
.footer-logo{font-family:var(--font-display);font-size:1rem;font-weight:800;color:var(--text);}
.footer-logo em{font-style:normal;color:var(--gold);}
footer p{font-size:0.72rem;color:var(--muted);font-family:var(--font-mono);}

/* RESPONSIVE */
@media(max-width:900px){
  .feature-grid{grid-template-columns:1fr;}
  .value-grid{grid-template-columns:1fr;}
  .testimonial-grid{grid-template-columns:1fr;}
  .proof-inner{justify-content:center;}
}
@media(max-width:600px){
  .form-grid{grid-template-columns:1fr;}
}
`;

export default function SubscribePage({ onNavigate }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", org: "", role: "", phone: "" });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch("https://legisim-server.onrender.com/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
    } catch {}
    setSubmitted(true);
  };

  const scrollToForm = () => {
    document.getElementById("subscribe-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <style>{CSS}</style>

      {/* NAV */}
      <nav className="nav">
        <div className="nav-logo" onClick={() => onNavigate && onNavigate("home")}>
          Whip<em>Count</em><sup>™</sup>
        </div>
        <button className="nav-back" onClick={() => onNavigate && onNavigate("home")}>← Back to Home</button>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-grid" />
        <div className="hero-inner">
          <div className="hero-eyebrow" style={{ animation: "none", opacity: 1 }}>
            <div className="hero-eyebrow-dot" />
            Michigan Legislative Intelligence
          </div>
          <h1>
            Stop guessing.<br />
            Start <em>knowing.</em>
          </h1>
          <p className="hero-sub">
            WhipCount™ gives Michigan lobbyists, trade associations, and government affairs professionals a complete platform — bill tracking, client management, AI-powered bill analysis, campaign finance data, a full directory, and a unified legislative calendar, all in one place.
          </p>
          <div className="hero-price-tag">
            <span className="hero-price">$99</span>
            <span className="hero-price-period">/ month</span>
          </div>
          <div>
            <button className="btn-subscribe" onClick={scrollToForm}>
              Reserve Your Access →
            </button>
          </div>
          <p className="hero-note">No long-term commitment · Cancel anytime · Bulk discounts for 5+ users</p>
        </div>
      </section>





      {/* VALUE PROPOSITION */}
      <section className="section value-section">
        <div className="section-inner">
          <div className="value-grid">
            <FadeIn>
              <span className="section-tag">Why WhipCount™</span>
              <h2 className="section-title">Replace the stack.<br />Keep the edge.</h2>
              <p className="section-sub" style={{ marginBottom: "2rem" }}>
                Most government affairs professionals juggle five or more tools to do what WhipCount™ does in one login. We built everything you need for a Michigan session into a single, fast, professional-grade platform.
              </p>
              <ul className="value-list">
                {[
                  { strong: "Manage clients without switching tools.", text: "The Client Portal tracks bills per client, logs meetings and hours, and generates print-ready client reports in seconds — powered by AI." },
                  { strong: "Bill intelligence that goes beyond tracking.", text: "Track bills with private notes, nicknames, and stances. Know instantly when status changes. Analyze any bill with plain-language AI summaries." },
                  { strong: "Your entire legislative calendar in one view.", text: "Committee meetings, fundraiser events from all four caucus committees, bill hearings, and session days — unified, filterable, and exportable." },
                  { strong: "Know the district, know the money.", text: "Interactive district maps and campaign finance data for every legislator — so you never have to open another site to understand who funds them and where they come from." },
                  { strong: "One platform, no context switching.", text: "WhipCount™ replaces the patchwork of tabs, spreadsheets, and PDFs that slow your team down during the most critical weeks of session." },
                ].map((item, i) => (
                  <li className="value-item" key={i}>
                    <div className="value-check">✓</div>
                    <div className="value-text">
                      <strong>{item.strong}</strong>
                      <span>{item.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="value-callout">
                <div className="callout-price">$99</div>
                <div className="callout-period">per month · billed monthly</div>
                <ul className="callout-items">
                  {[
                    "Bill Search & Tracking — all 3,200+ session bills",
                    "Legislative Calendar with fundraiser events",
                    "Client Portal — clients, meetings, activity & reports",
                    "Bill Studio — AI-powered bill analysis",
                    "Campaign Finance Data & District Maps",
                    "Michigan Government Affairs Directory",
                    "News Feed",
                    "Full 2025–2026 session coverage",
                    "Bulk discounts available for 5+ users",
                    "Cancel anytime",
                  ].map(item => (
                    <li className="callout-item" key={item}>{item}</li>
                  ))}
                </ul>
                <button className="btn-subscribe" style={{ width: "100%", textAlign: "center" }} onClick={scrollToForm}>
                  Reserve Your Access →
                </button>
                <p className="callout-note">Payment processing coming soon. Reserve now to be first in line.</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section">
        <div className="section-inner">
          <FadeIn>
            <span className="section-tag">What Professionals Say</span>
            <h2 className="section-title">Built for the people who<br />work <em>Lansing.</em></h2>
          </FadeIn>
          <div className="testimonial-grid">
            {TESTIMONIALS.map((t, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div className="testimonial-card">
                  <p className="testimonial-quote">"{t.quote}"</p>
                  <div className="testimonial-author">{t.author}</div>
                  <div className="testimonial-org">{t.org}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <hr className="divider" />

      {/* FAQ */}
      <section className="section">
        <div className="section-inner" style={{ maxWidth: 720 }}>
          <FadeIn>
            <span className="section-tag">Questions</span>
            <h2 className="section-title">Everything you need to know.</h2>
          </FadeIn>
          <div className="faq-list">
            {FAQS.map((faq, i) => (
              <div className="faq-item" key={i}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {faq.q}
                  <span className={`faq-icon${openFaq === i ? " open" : ""}`}>+</span>
                </button>
                <div className={`faq-a${openFaq === i ? " open" : ""}`}>
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUBSCRIBE FORM */}
      <section className="section subscribe-section" id="subscribe-form">
        <div className="section-inner" style={{ textAlign: "center" }}>
          <FadeIn>
            <span className="section-tag">Reserve Access</span>
            <h2 className="section-title">Join the waitlist.</h2>
            <p className="section-sub" style={{ margin: "0 auto" }}>
              Payment processing is being finalized. Submit your information now to secure your spot and be the first notified when WhipCount™ opens for subscriptions.
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="form-card">
              {submitted ? (
                <div className="success-card">
                  <div className="success-icon">✓</div>
                  <div className="success-title">You're on the list.</div>
                  <p className="success-sub">
                    We've received your information and will contact you as soon as payment processing is live — typically within a few business days.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-title">Reserve Your Subscription</div>
                  <p className="form-sub">$99/month · No commitment until billing is active</p>
                  <div className="form-grid">
                    <div className="field">
                      <label>First Name</label>
                      <input type="text" placeholder="Jane" required value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Last Name</label>
                      <input type="text" placeholder="Smith" required value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                    </div>
                    <div className="field form-full">
                      <label>Work Email</label>
                      <input type="email" placeholder="jane@yourfirm.com" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    </div>
                    <div className="field form-full">
                      <label>Organization</label>
                      <input type="text" placeholder="Association, Firm, or Corporation" required value={form.org} onChange={e => setForm(f => ({ ...f, org: e.target.value }))} />
                    </div>
                    <div className="field">
                      <label>Role</label>
                      <select required value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                        <option value="">Select...</option>
                        <option>Lobbyist / Government Affairs</option>
                        <option>Trade Association</option>
                        <option>Political Consultant</option>
                        <option>Corporate Affairs</option>
                        <option>Law Firm</option>
                        <option>PAC / Political Organization</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div className="field">
                      <label>Phone (optional)</label>
                      <input type="tel" placeholder="(555) 000-0000" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-notice">
                    <strong>No charge today.</strong> By submitting this form you are reserving your spot on the subscriber waitlist. You will be contacted to complete payment when billing is live. WhipCount™ is $99/month with no long-term commitment.
                  </div>
                  <button type="submit" className="btn-submit">Reserve My Access →</button>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="footer-logo">Whip<em>Count</em><sup>™</sup></div>
        <p>© {new Date().getFullYear()} WhipCount™. All rights reserved.</p>
        <p style={{ width: "100%", borderTop: "1px solid var(--border)", paddingTop: "1rem", marginTop: "0.5rem", lineHeight: 1.7, opacity: 0.6 }}>
          WhipCount™ is a decision-support tool. All outputs are predictive estimates based on publicly available legislative data and do not constitute legal, lobbying, or political advice. WhipCount™ is not affiliated with the Michigan Legislature or any government entity.
        </p>
      </footer>
    </>
  );
}
